import React, { forwardRef, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Box, ToggleButton, ToggleButtonGroup, IconButton, TextField, Autocomplete } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { enUS, ru, hy } from 'date-fns/locale'
import { operatorReportStyles as opStyles, inputStyles, selectStyles, reportPrimary, reportHover } from '../../../helpers/style'
import { PERIODS } from '../../../helpers/demo'
import { hasReportViewAll } from '../../../helpers/employeePermissions'

registerLocale('en', enUS)
registerLocale('ru', ru)
registerLocale('hy', hy)

const DateInput = forwardRef(({ value, onClick, ...rest }, ref) => (
  <input
    ref={ref}
    value={value ?? ''}
    onClick={onClick}
    readOnly
    style={{
      padding: '0 14px',
      height: 38,
      width: '100%',
      minWidth: 200,
      boxSizing: 'border-box',
      border: 'none',
      fontSize: 14,
      outline: 'none',
      backgroundColor: 'transparent',
      fontFamily: 'inherit',
    }}
    {...rest}
  />
))
DateInput.displayName = 'DateInput'

const DIRECTION_KEYS = ['incoming', 'outgoing', 'local', 'transferred']

export default function ReportPeriodRow({
  period,
  onPeriodChange,
  onRefresh,
  loading,
  refreshing,
  getPeriodLabel,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onCustomRangeSelected,
  employees,
  selectedEmployee,
  onEmployeeChange,
  opTr,
  langIndex,
  showDirectionToggle,
  statusDirection,
  onStatusDirectionChange,
  getDirectionLabel,
  showEmployeeSelect = true,
}) {
  const isCustom = period === 'custom'
  const employeeData = useSelector((state) => state.users.employeeData)
  const canShowEmployeeSelect = showEmployeeSelect && hasReportViewAll(employeeData)

  const datePickerLocale = useMemo(() => {
    if (langIndex === 2) return 'ru'
    if (langIndex === 3) return 'hy'
    return 'en'
  }, [langIndex])

  const getEmployeeLabel = (emp) => {
    if (!emp) return ''
    const notes = emp.notes != null ? String(emp.notes).trim() : ''
    if (notes) return notes
    return [emp.name, emp.fio, emp.buddy].filter(Boolean).join(' ') || String(emp.buddy || emp.id || '')
  }

  const customDateLabel = isCustom && dateFrom && dateTo
    ? `${dateFrom} - ${dateTo}`
    : opTr.periodCustom?.[langIndex] ?? 'Custom date'

  return (
    <Box sx={opStyles.periodRow}>
      <Box sx={opStyles.periodRowGroup}>
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={(_, newPeriod) => {
              if (newPeriod) {
                onPeriodChange(newPeriod)
              } else if (period !== 'custom') {
                // Re-clicking the same period (except custom) should trigger refresh
                onPeriodChange(period)
              }
            }}
            size="small"
            sx={{
              ...opStyles.toggleButtonGroup,
              '& .MuiToggleButton-root.Mui-selected:hover': {
                backgroundColor: reportHover,
              },
            }}
          >
            {PERIODS.map((p) => {
              if (p.value === 'custom') {
                const label = customDateLabel
                return (
                  <ToggleButton 
                    key={p.value} 
                    value={p.value}
                    sx={{ position: 'relative', overflow: 'visible' }}
                  >
                    {label}
                    {/* DatePicker overlay on the custom button */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        '& .react-datepicker-wrapper': { width: '100%', height: '100%' },
                        '& .react-datepicker__input-container': { width: '100%', height: '100%' },
                        '& input': {
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: 'pointer',
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (!isCustom) {
                          onPeriodChange('custom')
                        }
                      }}
                    >
                      <DatePicker
                        selected={dateFrom ? new Date(dateFrom) : null}
                        onChange={(dates) => {
                          if (!isCustom) {
                            onPeriodChange('custom')
                          }
                          const [start, end] = dates
                          onDateFromChange(start ? start.toISOString().slice(0, 10) : null)
                          onDateToChange(end ? end.toISOString().slice(0, 10) : null)
                          if (start != null && end != null) onCustomRangeSelected?.()
                        }}
                        startDate={dateFrom ? new Date(dateFrom) : null}
                        endDate={dateTo ? new Date(dateTo) : null}
                        selectsRange
                        dateFormat="yyyy-MM-dd"
                        disabled={loading}
                        monthsShown={2}
                        showPreviousMonths
                        openToDate={dateTo ? new Date(dateTo) : (dateFrom ? new Date(dateFrom) : new Date())}
                        calendarClassName="operator-report-datepicker operator-report-datepicker--two-months"
                        locale={datePickerLocale}
                      />
                    </Box>
                  </ToggleButton>
                )
              }
              return (
                <ToggleButton 
                  key={p.value} 
                  value={p.value}
                  onClick={() => onPeriodChange(p.value)}
                >
                  {getPeriodLabel(p.value)}
                </ToggleButton>
              )
            })}
          </ToggleButtonGroup>
        </Box>
        {showDirectionToggle && (
          <ToggleButtonGroup
            value={statusDirection ?? 'incoming'}
            exclusive
            onChange={(_, v) => v && onStatusDirectionChange?.(v)}
            size="small"
            sx={{
              ...opStyles.toggleButtonGroup,
              '& .MuiToggleButton-root.Mui-selected:hover': {
                backgroundColor: reportHover,
              },
            }}
          >
            {DIRECTION_KEYS.map((key) => (
              <ToggleButton key={key} value={key}>
                {getDirectionLabel ? getDirectionLabel(key) : (opTr[`dir${key.charAt(0).toUpperCase() + key.slice(1)}`]?.[langIndex] ?? key)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </Box>

      <Box sx={opStyles.periodRowGroup}>
        {canShowEmployeeSelect && (
          <Box sx={{ ...opStyles.filterField, minWidth: 200 }}>
            <Autocomplete
              size="small"
              options={employees || []}
              value={selectedEmployee ?? null}
              onChange={(_, value) => onEmployeeChange(value)}
              getOptionLabel={getEmployeeLabel}
              isOptionEqualToValue={(opt, val) => (opt?.id && val?.id ? opt.id === val.id : opt?.buddy === val?.buddy)}
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={opTr.employee?.[langIndex] ?? 'Employee'}
                  placeholder={opTr.selectEmployee?.[langIndex] ?? 'Select employee'}
                  sx={{
                    ...inputStyles,
                    ...selectStyles,
                    '& .MuiOutlinedInput-root': { borderRadius: 1 },
                    '& .MuiOutlinedInput-root fieldset': { borderColor: reportPrimary },
                    '& .MuiOutlinedInput-root:hover fieldset': { borderColor: reportHover },
                    '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: reportPrimary },
                  }}
                />
              )}
            />
          </Box>
        )}
        <IconButton
          onClick={onRefresh}
          disabled={loading || refreshing}
          sx={opStyles.refreshButton}
          aria-label="Refresh"
          size="small"
        >
          <RefreshIcon sx={{ fontSize: 22, ...(refreshing && { animation: 'spin 1s linear infinite' }) }} />
        </IconButton>
      </Box>
    </Box>
  )
}
